import React, { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import clamp from 'lodash/clamp';
import isEmpty from 'lodash/isEmpty';
import partialRight from 'lodash/partialRight';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LoadingIcon } from '@pkg/assets/icons/v2/loading-bars.svg';
import { ReactComponent as SearchIcon } from '@pkg/assets/icons/v2/search.svg';

import { DepInput, type DepInputProps } from '../DepInput';

import './DepSearch.scss';

/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: We probably don't want to allow all of the `DepInputProps`.
export interface DepSearchProps extends DepInputProps {
    query?: string;
    searchResults: Array<any>;
    loading?: boolean;
    resultsFloat?: boolean;
    /** Callback to handle the selection of a currency */
    onQueryChange: (query: string) => void;
    onSelect?: (result: any) => void;
    Result?: (result: any, index?: number) => ReactElement | null;
}

function keyGenerator(index = 0) {
    return `result-${index}`;
}

function DefaultResult(result) {
    return <div>{JSON.stringify(result)}</div>;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export function DepSearch({
    query = '',
    searchResults = [],
    loading = false,
    // TODO: `boolean` props should default to `false`.
    resultsFloat = true,
    onQueryChange,
    onSelect,
    Result = DefaultResult,
    ...props
}: DepSearchProps) {
    const { t } = useTranslation(['common']);

    const searchRef = useRef(null);

    const [isTyping, setIsTyping] = useState(false);
    const [highlightedSearchResultIndex, setHighlightedSearchResultIndex] = useState(0);

    // TODO: Consider restoring this once we can better control
    // "Results state" in relation to the <EmojiPicker />.
    // const [isFocused, setIsFocused] = useState(false);

    const selectResultAtIndex = useCallback(
        function (indexToSelect) {
            const resultToSelect = searchResults[indexToSelect];
            onSelect?.(resultToSelect);
        },
        [searchResults, onSelect],
    );

    const selectHighlightedSearchResult = useCallback(
        function () {
            selectResultAtIndex(highlightedSearchResultIndex);
        },
        [selectResultAtIndex, highlightedSearchResultIndex],
    );

    useEffect(() => {
        setHighlightedSearchResultIndex(0);
    }, [setHighlightedSearchResultIndex, searchResults]);

    useEffect(() => {
        if (!isTyping) return;

        const typingTimeoutId = setTimeout(() => setIsTyping(false), 250);

        return () => {
            if (typingTimeoutId) clearTimeout(typingTimeoutId);
        };
    }, [isTyping]);

    const highlightSearchResultAtIndex = useCallback(
        function (indexToHighlight) {
            setHighlightedSearchResultIndex(clamp(indexToHighlight, 0, searchResults.length - 1));
        },
        [setHighlightedSearchResultIndex, searchResults],
    );

    const highlightNextSearchResult = useCallback(
        function () {
            highlightSearchResultAtIndex(highlightedSearchResultIndex + 1);
        },
        [highlightSearchResultAtIndex, highlightedSearchResultIndex],
    );

    const highlightPreviousSearchResult = useCallback(
        function () {
            highlightSearchResultAtIndex(highlightedSearchResultIndex - 1);
        },
        [highlightSearchResultAtIndex, highlightedSearchResultIndex],
    );

    function handleSearchResultMouseEnter(event, index) {
        setHighlightedSearchResultIndex(index);
    }

    function handleQueryChange(event) {
        onQueryChange(event.target.value);
    }

    function handleKeyDown(event) {
        switch (event.key) {
            case 'ArrowDown':
                highlightNextSearchResult();
                break;
            case 'ArrowUp':
                highlightPreviousSearchResult();
                break;
            case 'Enter':
                selectHighlightedSearchResult();
                break;
            default:
                setIsTyping(true);
                return;
        }
    }

    const handleClick = useCallback(
        function (index) {
            selectResultAtIndex(index);
        },
        [selectResultAtIndex],
    );

    const shouldShowLoading = loading;
    const shouldShowNoResults = !loading && isEmpty(searchResults) && query && !isTyping;
    const shouldShowResults = !loading && !isEmpty(searchResults);

    const searchInputClassNames = cx('yat-search-input', {
        'yat-search-input--searching': !isEmpty(query),
        'yat-search-input--results-do-not-float': !resultsFloat,
    });

    const resultsMarkup =
        query && (shouldShowLoading || shouldShowNoResults || shouldShowResults) ? (
            <div key="results-origin" className="yat-search-input__results-origin">
                <motion.div className="yat-search-input__results" layout>
                    {loading && (
                        <span className="yat-search-input__loader" aria-label="Loading">
                            <LoadingIcon aria-hidden="true" />
                        </span>
                    )}

                    {!loading && isEmpty(searchResults) && query && !isTyping && (
                        <div className="yat-search-input__results-empty">{t('search_no_results', { query })}</div>
                    )}

                    {!loading &&
                        !isEmpty(searchResults) &&
                        searchResults.map((result, resultIndex) => (
                            <Result
                                {...result}
                                index={resultIndex}
                                onClick={partialRight(handleClick, resultIndex)}
                                onMouseEnter={partialRight(handleSearchResultMouseEnter, resultIndex)}
                                key={keyGenerator(resultIndex)}
                            />
                        ))}
                </motion.div>
            </div>
        ) : null;

    return (
        <div ref={searchRef} className={searchInputClassNames}>
            <DepInput
                classes="yat-search-input__input"
                value={query}
                append={<SearchIcon className="yat-search-input__search-icon" />}
                isInvalid={false}
                onChange={handleQueryChange}
                onKeyDown={handleKeyDown}
                // onFocus={() => setIsFocused(true)}
                // onBlur={() => setIsFocused(false)}
                // TODO: We should be more prescriptive about the props
                // this components accepts.
                {...props}
            />

            <AnimatePresence>{resultsMarkup}</AnimatePresence>
        </div>
    );
}
